<template>
  <div>
    <div class="overlay" v-if="loading"></div>

    <div class="content_wrapper">
        <!-- HEADER -->
        <div class="header_section">
          <div class="header_texts">
            <div>
              <h1 class="page_title">Devices</h1>
              <p class="section_desc">Devices registered to your branch.</p>
            </div>
            <div class="role_badge">
              <span class="role_label">Current Role:</span>
              <span class="role_value">{{ currentRole }}</span>
            </div>
          </div>
        </div>

        <!-- SEARCH + FILTER -->
        <div class="toolbar_section">
          <div class="search_filter_group">
            <div class="search_group">
              <svg class="search_icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input type="text" class="clean_search_input" id="devicename"
                placeholder="Search by serial number or name…" v-model="devicename" @keyup.enter="searchDevice" />
            </div>
            <div class="filter_group">
              <select @change="onFilterChange" class="container_input filter_select" v-model="select_device_option">
                <option value="ios">iOS</option>
                <option value="ipados">iPadOS</option>
                <option value="macos">MacOS</option>
              </select>
            </div>
          </div>
        </div>

        <!-- TABLE -->
        <div class="premium_card table_card">
          <!-- Mobile cards -->
          <div class="mobile_card_list">
            <div class="device_card" v-for="(item, index) in processedTableData" :key="index">
              <div class="card_header">
                <span class="device_name_mobile">{{ item.deviceName }}</span>
                <span class="status_badge"
                  :class="item.pending ? 'status_pending' : (item.locked === 0 ? 'status_active' : 'status_locked')">
                  {{ item.pending ? "Pending" : (item.locked === 0 ? "Disabled" : "Enabled") }}
                </span>
              </div>
              <div class="card_body">
                <div class="info_row"><span class="label">User:</span><span class="value">{{ item.usern }}</span></div>
                <div class="info_row"><span class="label">SN:</span><span class="value mono_text">{{ item.serialNumber }}</span></div>
                <div class="info_row"><span class="label">MSISDN:</span><span class="value">{{ item.msisdn }}</span></div>
              </div>
            </div>
            <div v-if="processedTableData.length === 0" class="empty_mobile_state">No devices found.</div>
          </div>

          <!-- Desktop table -->
          <div class="table_responsive desktop_only">
            <table class="premium_table">
              <thead>
                <tr>
                  <th>No.</th>
                  <th @click="sortBy('usern')" class="sortable">
                    <div class="th_content">User
                      <span class="sort_indicator" v-if="sortKey === 'usern'">{{ sortOrder === "asc" ? "▲" : "▼" }}</span>
                    </div>
                  </th>
                  <th @click="sortBy('deviceName')" class="sortable">
                    <div class="th_content">Device Name
                      <span class="sort_indicator" v-if="sortKey === 'deviceName'">{{ sortOrder === "asc" ? "▲" : "▼" }}</span>
                    </div>
                  </th>
                  <th class="mono_text">Serial Number</th>
                  <th>MSISDN</th>
                  <th>Last Seen</th>
                  <th @click="toggleStatusFilter" class="filter_header">
                    <div class="th_content">Lost Mode Status
                      <span class="filter_indicator" v-if="statusFilter !== 'all'">({{ statusFilter }})</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in processedTableData" :key="index">
                  <td>{{ index + 1 }}</td>
                  <td>{{ item.usern }}</td>
                  <td><span class="device_name">{{ item.deviceName }}</span></td>
                  <td class="mono_text">{{ item.serialNumber }}</td>
                  <td>{{ item.msisdn }}</td>
                  <td><span class="last_seen_text">{{ item.last_seen_readable || '-' }}</span></td>
                  <td>
                    <span class="status_badge"
                      :class="item.pending ? 'status_pending' : (item.locked === 0 ? 'status_active' : 'status_locked')">
                      {{ item.pending ? "Pending" : (item.locked === 0 ? "Disabled" : "Enabled") }}
                    </span>
                  </td>
                </tr>
                <tr v-if="processedTableData.length === 0">
                  <td colspan="7" class="empty_state">No devices found.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="table_footer">
            <p>Showing <b>{{ processedTableData.length }}</b> result(s)</p>
          </div>
        </div>
      </div>

    <!-- Access denied -->
    <div class="loading-card" v-if="page_unavailable">
      <div class="loading-content">
        <div class="modal_icon_wrapper"><i class="bx bx-error-circle modal_icon_font"></i></div>
        <h3 style="color: var(--error-color)">Access Denied</h3>
        <p>You do not have permission to view this page.</p>
        <button class="primary_btn" @click="goBack">Back</button>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  data() {
    return {
      devicename: "",
      loading: true,
      page_unavailable: false,
      currentRole: localStorage.role,
      select_device_option: "ios",
      tableData: [],
      sortKey: "usern",
      sortOrder: "asc",
      statusFilter: "all", // all | pending | enabled | disabled
    };
  },
  computed: {
    processedTableData() {
      let data = [...this.tableData];
      if (this.statusFilter === "pending") {
        data = data.filter((item) => item.pending === true);
      } else if (this.statusFilter === "enabled") {
        data = data.filter((item) => item.locked === 1 && !item.pending);
      } else if (this.statusFilter === "disabled") {
        data = data.filter((item) => item.locked === 0 && !item.pending);
      }
      if (this.sortKey) {
        data.sort((a, b) => {
          let aVal = a[this.sortKey];
          let bVal = b[this.sortKey];
          if (aVal == null) aVal = "";
          if (bVal == null) bVal = "";
          if (typeof aVal === "string") aVal = aVal.toLowerCase();
          if (typeof bVal === "string") bVal = bVal.toLowerCase();
          if (aVal < bVal) return this.sortOrder === "asc" ? -1 : 1;
          if (aVal > bVal) return this.sortOrder === "asc" ? 1 : -1;
          return 0;
        });
      }
      return data;
    },
  },
  methods: {
    goBack() {
      this.page_unavailable = false;
      this.loading = false;
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
    },
    toggleStatusFilter() {
      if (this.statusFilter === "all") this.statusFilter = "pending";
      else if (this.statusFilter === "pending") this.statusFilter = "enabled";
      else if (this.statusFilter === "enabled") this.statusFilter = "disabled";
      else this.statusFilter = "all";
    },
    onFilterChange() {
      this.devicename = "";
      this.statusFilter = "all";
      this.refreshDeviceInfo();
    },
    searchDevice() {
      this.refreshDeviceInfo();
    },
    refreshDeviceInfo(serialOverride) {
      this.page_unavailable = false;
      const axios = require("axios");
      let sn = serialOverride !== undefined ? serialOverride : this.devicename;

      const data = JSON.stringify({
        token: localStorage.token,
        serial_number: sn,
        device_type: this.select_device_option,
      });
      axios.request({
        method: "POST",
        maxBodyLength: Infinity,
        url: process.env.VUE_APP_BACKEND_URL + "/v1/dashboard/display-specific-devices/serial-number",
        headers: {
          Authorization: `Bearer ${localStorage.bearer_token}`,
          "Content-Type": "application/json",
        },
        data: data,
      }).then((response) => {
        if (response.status === 200) {
          this.loading = false;
          this.tableData = (response.data.devices || []).map((item) => ({
            deviceName: item.name_phone,
            serialNumber: item.serial_number,
            msisdn: item.msisdn,
            usern: item.name_user,
            locked: item.is_Locked,
            pending: item.pending,
            last_seen_readable: item.last_seen_readable,
          }));
        } else {
          this.loading = false;
          this.page_unavailable = true;
        }
      }).catch((error) => {
        console.log(error);
        this.loading = false;
        this.page_unavailable = true;
      });
    },
  },
  mounted() {
    this.refreshDeviceInfo("");
  },
};
</script>

<style scoped>
.content_wrapper {
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

/* Header */
.header_section { display: flex; align-items: flex-start; gap: var(--spacing-md); margin-bottom: var(--spacing-sm); }
.header_texts { display: flex; justify-content: space-between; align-items: center; flex: 1; }
.page_title {
  font-family: var(--font-family-header);
  font-weight: 800; font-size: 32px; color: var(--text-main); margin: 0; line-height: 1.2;
}
.section_desc { font-family: var(--font-family-base); font-size: 14px; color: var(--text-muted); line-height: 1.6; margin: var(--spacing-xs) 0 0 0; }
.role_badge {
  background-color: var(--surface-color); padding: 8px 16px; border-radius: 50px; font-size: 14px;
  display: flex; gap: 8px; box-shadow: var(--shadow-sm); align-items: center;
}
.role_label { color: var(--text-muted); font-weight: 500; }
.role_value { color: var(--text-main); font-weight: 700; }

/* Toolbar */
.toolbar_section {
  display: flex; justify-content: flex-end; align-items: center; gap: var(--spacing-lg);
  flex-wrap: wrap-reverse; margin-bottom: var(--spacing-md);
}
.search_filter_group { display: flex; gap: var(--spacing-md); align-items: center; margin-left: auto; }
.search_group { position: relative; display: flex; align-items: center; }
.search_icon { position: absolute; left: 12px; color: var(--text-muted); pointer-events: none; }
.clean_search_input {
  padding: 10px 16px 10px 40px; border: 1px solid var(--border-color); border-radius: 50px;
  font-family: var(--font-family-base); font-size: 14px; width: 280px; transition: all 0.2s;
  background-color: var(--surface-color);
}
.clean_search_input:focus {
  width: 320px; outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(255, 214, 0, 0.1);
}
.container_input {
  padding: 10px 16px; border: 1px solid var(--border-color); border-radius: var(--border-radius-md);
  font-family: var(--font-family-base); font-size: 14px; color: var(--text-main); background-color: var(--surface-color);
}
.container_input:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 2px rgba(255, 214, 0, 0.2); }
.filter_select {
  width: auto; min-width: 120px; cursor: pointer; appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat; background-position: right 0.7rem center; background-size: 1em; padding-right: 2.5rem;
}

/* Sort / filter headers */
.sortable, .filter_header { cursor: pointer; user-select: none; }
.sort_indicator { font-size: 10px; margin-left: 4px; color: var(--primary-color); }
.filter_indicator { font-size: 11px; font-weight: 500; color: var(--primary-color); text-transform: none; }

/* Table */
.premium_card {
  background-color: var(--surface-color); border-radius: var(--border-radius-lg); box-shadow: var(--shadow-md);
  overflow: hidden; padding: 0; display: flex; flex-direction: column;
}
.table_responsive { width: 100%; overflow-x: auto; }
.premium_table { width: 100%; border-collapse: collapse; text-align: left; }
.premium_table th {
  background-color: #fafafa; padding: 16px 20px; font-family: var(--font-family-header); font-weight: 700;
  font-size: 12px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em;
  border-bottom: 1px solid var(--border-color); vertical-align: middle;
}
.th_content { display: flex; align-items: center; gap: 6px; white-space: nowrap; }
.premium_table td {
  padding: 16px 20px; border-bottom: 1px solid #f3f4f6; font-family: var(--font-family-base);
  font-size: 14px; color: var(--text-main); vertical-align: middle;
}
.premium_table tbody tr:last-child td { border-bottom: none; }
.premium_table tbody tr:hover { background-color: #fffbeb; }
.device_name { font-weight: 600; color: var(--text-main); }
.mono_text { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; color: var(--text-muted); font-size: 13px; }

/* Status badges */
.status_badge { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }
.status_active { background-color: #dcfce7; color: #166534; }
.status_locked { background-color: #fee2e2; color: #991b1b; }
.status_pending { background-color: #fffbeb; color: #d97706; }

.empty_state { text-align: center; padding: 40px !important; color: var(--text-muted); }
.last_seen_text { font-size: 13px; color: var(--text-main); white-space: nowrap; }

.table_footer {
  padding: 16px 20px; border-top: 1px solid var(--border-color); background-color: #fafafa;
  font-size: 13px; color: var(--text-muted); display: flex; justify-content: flex-end;
}

.primary_btn {
  background-color: var(--primary-color); color: var(--text-main); padding: 10px 24px; border: none;
  border-radius: 50px; font-weight: 700; cursor: pointer;
}

/* Mobile cards */
.mobile_card_list { display: none; flex-direction: column; gap: 15px; padding: 10px; }
.device_card { background: #fff; border: 1px solid var(--border-color); border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); overflow: hidden; }
.card_header { display: flex; justify-content: space-between; align-items: center; padding: 12px 15px; border-bottom: 1px solid #f0f0f0; background-color: #fafafa; }
.device_name_mobile { font-weight: 700; color: var(--text-main); font-size: 15px; }
.card_body { padding: 15px; display: flex; flex-direction: column; gap: 8px; }
.info_row { display: flex; justify-content: space-between; font-size: 13px; }
.info_row .label { color: var(--text-muted); }
.info_row .value { color: var(--text-main); font-weight: 500; text-align: right; }
.empty_mobile_state { text-align: center; padding: 30px; color: var(--text-muted); background: white; border-radius: 12px; }

/* Access-denied card */
.loading-card { position: fixed; z-index: 9999; width: 400px; top: 50%; left: 50%; transform: translate(-50%, -50%); background-color: white; padding: 30px; border-radius: 28px; box-shadow: var(--shadow-lg); text-align: center; }
.loading-content { display: flex; flex-direction: column; align-items: center; }
.modal_icon_wrapper { display: flex; justify-content: center; align-items: center; margin-bottom: 24px; }
.modal_icon_font { font-size: 80px; color: #ff3b30; }
.overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0,0,0,0.5); z-index: 9999; }

@media (max-width: 768px) {
  .desktop_only { display: none !important; }
  .mobile_card_list { display: flex; }
  .premium_card.table_card { background: transparent; box-shadow: none; border: none; padding: 0; }
  .header_texts { flex-direction: column; align-items: flex-start; gap: 15px; }
  .toolbar_section { flex-direction: column; align-items: stretch; gap: 15px; }
  .search_filter_group { flex-direction: column; width: 100%; margin-left: 0; }
  .search_group, .clean_search_input, .clean_search_input:focus, .filter_select { width: 100%; }
}
</style>
